// VersionHistory — ONE version-history panel for every first-class component
// (apps, agents, models, workflows).
//
// Two stores sit behind it, deliberately hidden from the caller:
//   git    — apps/agents/models are Flow endpoints on the paired fork, so their
//            history is the fork's export commits (a durable LOCAL repo; no
//            remote is required, and agents commit on change).
//   engine — a workflow is `wf_*` postgres rows, not an endpoint, so git cannot
//            see it; the process engine keeps its own append-only version table.
// The response carries `source`, and the only visible difference is the label on
// a row ("commit abc1234" vs "v3") — everything else renders identically.
import { useCallback, useEffect, useState } from "react";
import {
  History, GitCommitHorizontal, Loader2, RefreshCw, ChevronRight,
  RotateCcw, AlertTriangle, FileDiff,
} from "lucide-react";
import { listVersions, getVersionDiff, restoreVersion } from "../services/versions";

const shortSha = (s) => (s ? String(s).slice(0, 10) : "—");

function fmtTime(v) {
  if (!v) return "";
  try {
    return new Date(v).toLocaleString(undefined, {
      month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return String(v); }
}

function errMsg(e) { return String(e?.message || e?.error || e || "request failed"); }

// What a version did to this component, relative to the live definition.
const STATUS_STYLE = {
  changed: { label: "changed", cls: "text-amber-600 dark:text-amber-400" },
  modified: { label: "changed", cls: "text-amber-600 dark:text-amber-400" },
  new: { label: "added", cls: "text-emerald-600 dark:text-emerald-400" },
  removed: { label: "removed", cls: "text-red-500" },
  // A project version is a whole-fork snapshot; nothing is "changed" about it.
  snapshot: { label: "snapshot", cls: "text-page-text/50" },
  unchanged: { label: "same as now", cls: "text-page-text/40" },
  unreadable: { label: "unreadable", cls: "text-page-text/40" },
  saved: { label: "saved", cls: "text-page-text/60" },
  published: { label: "published", cls: "text-emerald-600 dark:text-emerald-400" },
  unpublished: { label: "unpublished", cls: "text-page-text/60" },
  restored: { label: "restored", cls: "text-sky-600 dark:text-sky-400" },
};

function StatusTag({ status }) {
  const s = STATUS_STYLE[status] || { label: status || "—", cls: "text-page-text/50" };
  return <span className={`text-[10px] uppercase tracking-wide ${s.cls}`}>{s.label}</span>;
}

// One row's diff detail, fetched on expand (a diff costs an engine round-trip,
// so it is never prefetched for the whole list).
function DiffDetail({ kind, name, row, source, projectSlug }) {
  const [state, setState] = useState({ loading: true, error: "", change: null });

  useEffect(() => {
    let alive = true;
    setState({ loading: true, error: "", change: null });
    getVersionDiff({
      kind, name, projectSlug,
      ...(source === "engine" ? { version: row.version } : { commitHash: row.commit }),
    })
      // `change` for a component, `version` for a workflow, and the response
      // itself for a project (whose detail is the whole commit).
      .then((r) => { if (alive) setState({ loading: false, error: "", change: r?.change || r?.version || r || null }); })
      .catch((e) => { if (alive) setState({ loading: false, error: errMsg(e), change: null }); });
    return () => { alive = false; };
  }, [kind, name, row.commit, row.version, source, projectSlug]);

  if (state.loading) {
    return (
      <div className="px-3 py-2 text-xs text-page-text/60 flex items-center gap-1.5">
        <Loader2 size={12} className="animate-spin" /> loading changes…
      </div>
    );
  }
  if (state.error) {
    return <div className="px-3 py-2 text-xs text-red-500">{state.error}</div>;
  }
  const ch = state.change || {};

  // A PROJECT version's detail is which objects the commit touches, not one
  // object's node-level hunks — different question, different shape.
  if (kind === "project") {
    const eps = Array.isArray(ch.endpoints) ? ch.endpoints : [];
    const svcs = Array.isArray(ch.services) ? ch.services : [];
    if (!eps.length && !svcs.length) {
      return <div className="px-3 py-2 text-xs text-page-text/60">Identical to the current project state.</div>;
    }
    return (
      <div className="px-3 py-2 grid gap-1">
        {[...eps, ...svcs].slice(0, 60).map((r, i) => (
          <div key={`${r.name}-${i}`} className="text-xs flex items-center gap-2">
            <FileDiff size={11} className="text-page-text/50 shrink-0" />
            <span className="font-mono text-page-text truncate">{r.name}</span>
            <StatusTag status={r.status} />
          </div>
        ))}
        {(eps.length + svcs.length) > 60 && (
          <div className="text-[11px] text-page-text/40">
            +{eps.length + svcs.length - 60} more
          </div>
        )}
      </div>
    );
  }

  const nodeDiffs = Array.isArray(ch.nodeDiffs) ? ch.nodeDiffs : [];
  const otherDiffs = Array.isArray(ch.otherDiffs) ? ch.otherDiffs : [];

  if (!nodeDiffs.length && !otherDiffs.length) {
    return (
      <div className="px-3 py-2 text-xs text-page-text/60">
        No field-level differences from the current definition.
      </div>
    );
  }
  return (
    <div className="px-3 py-2 grid gap-1.5">
      {otherDiffs.length > 0 && (
        <div className="text-xs text-page-text/70">
          Fields: {otherDiffs.map((d) => d?.field).filter(Boolean).join(", ")}
        </div>
      )}
      {nodeDiffs.map((d, i) => (
        <div key={`${d.nodeId || i}-${i}`} className="text-xs">
          <div className="flex items-center gap-2">
            <FileDiff size={11} className="text-page-text/50 shrink-0" />
            <span className="font-medium text-page-text truncate">{d.label || d.nodeId}</span>
            <StatusTag status={d.status} />
            {d.field && <span className="text-page-text/50">{d.field}</span>}
          </div>
          {Array.isArray(d.hunks) && d.hunks.length > 0 && (
            <pre className="mt-1 ml-4 max-h-48 overflow-auto rounded bg-black/5 dark:bg-white/5 p-2 font-mono text-[11px] leading-snug">
              {d.hunks.map((h, hi) => {
                const text = typeof h === "string" ? h : (h?.text ?? h?.line ?? JSON.stringify(h));
                return <div key={hi}>{text}</div>;
              })}
            </pre>
          )}
        </div>
      ))}
    </div>
  );
}

/**
 * @param kind  'app' | 'agent' | 'model' | 'workflow'
 * @param name  the component's identifying name (a *.view endpoint name for
 *              apps, the bare name for agents/models, the definition id for
 *              workflows)
 * @param title optional heading override
 * @param canRestore  show the restore control (callers pass their own write check)
 * @param onRestored  called after a successful restore so the page can refetch
 */
export default function VersionHistory({ kind, name, title = "Version history", canRestore = false, onRestored, projectSlug }) {
  const [state, setState] = useState({ loading: true, error: "", data: null });
  const [openRow, setOpenRow] = useState(null);
  const [restoring, setRestoring] = useState("");
  const [confirm, setConfirm] = useState(null);

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: "" }));
    try {
      const data = await listVersions({ kind, name, projectSlug });
      setState({ loading: false, error: "", data });
    } catch (e) {
      setState({ loading: false, error: errMsg(e), data: null });
    }
  }, [kind, name, projectSlug]);

  useEffect(() => { if (kind && name) load(); }, [kind, name, load]);

  const source = state.data?.source || "git";
  const versions = Array.isArray(state.data?.versions) ? state.data.versions : [];
  const rowKey = (v) => (source === "engine" ? `v${v.version}` : v.commit);

  // A PROJECT restore rewrites the whole fork, so it goes through the confirm
  // step; every other kind restores just its own object and can act directly.
  const projectWide = kind === "project";

  async function doRestore(row) {
    setRestoring(rowKey(row));
    try {
      await restoreVersion({
        kind, name, projectSlug,
        ...(source === "engine" ? { version: row.version } : { commitHash: row.commit }),
        ...(projectWide ? { acknowledge: true } : {}),
      });
      setConfirm(null);
      await load();
      onRestored?.();
    } catch (e) {
      setState((s) => ({ ...s, error: errMsg(e) }));
      setConfirm(null);
    } finally {
      setRestoring("");
    }
  }

  return (
    <div className="rounded-lg border border-card-border bg-card-bg" data-testid="version-history">
      <div className="flex items-center justify-between px-3 py-2 border-b border-card-border">
        <h3 className="text-sm font-semibold text-page-text flex items-center gap-1.5">
          <History size={14} /> {title}
        </h3>
        <button type="button" onClick={load} title="Refresh"
                className="p-1 rounded hover:bg-black/5 dark:hover:bg-white/5 text-page-text/60">
          {state.loading ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
        </button>
      </div>

      {state.error && (
        <div className="px-3 py-2 text-xs text-red-500 flex items-start gap-1.5">
          <AlertTriangle size={12} className="mt-0.5 shrink-0" />
          <span>{state.error}</span>
        </div>
      )}

      {!state.loading && !state.error && versions.length === 0 && (
        <div className="px-3 py-3 text-sm text-page-text/60">
          {state.data?.totalCommits
            ? `No recorded changes to this ${kind} across the project's full history `
              + `(${state.data.totalCommits} commits).`
            : `No recorded versions yet for this ${kind}.`}
        </div>
      )}

      <div className="divide-y divide-card-border">
        {versions.map((v) => {
          const key = rowKey(v);
          const isOpen = openRow === key;
          return (
            <div key={key} data-version={key}>
              <div className="px-3 py-2 flex items-center gap-2">
                <button type="button" onClick={() => setOpenRow(isOpen ? null : key)}
                        className="p-0.5 rounded hover:bg-black/5 dark:hover:bg-white/5 text-page-text/50 shrink-0"
                        aria-label={isOpen ? "Hide changes" : "Show changes"}>
                  <ChevronRight size={13} className={`transition-transform ${isOpen ? "rotate-90" : ""}`} />
                </button>

                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 text-sm">
                    {source === "engine" ? (
                      <span className="font-medium text-page-text">v{v.version}</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 font-medium text-page-text"
                            title={`Commit ${v.commit}`}>
                        <GitCommitHorizontal size={12} />
                        <code className="font-mono text-xs">{v.short || shortSha(v.commit)}</code>
                      </span>
                    )}
                    <StatusTag status={source === "engine" ? v.change : v.status} />
                    {v.current && (
                      <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-nav-active text-white">
                        current
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-page-text/60 mt-0.5 truncate">
                    {v.message || v.note || (source === "engine" ? `${v.node_count ?? "?"} steps` : "")}
                  </div>
                  <div className="text-[11px] text-page-text/40 mt-0.5 flex flex-wrap gap-x-3">
                    {(v.author || v.created_by) && <span>{v.author || v.created_by}</span>}
                    <span>{fmtTime(v.date || v.created_at)}</span>
                    {v.changed_nodes > 0 && <span>{v.changed_nodes} node(s) differ</span>}
                  </div>
                </div>

                {canRestore && (
                  <button type="button" onClick={() => (projectWide ? setConfirm(v) : doRestore(v))}
                          disabled={!!restoring}
                          className="shrink-0 text-xs px-2 py-1 rounded border border-card-border text-page-text/70
                                     hover:bg-black/5 dark:hover:bg-white/5 disabled:opacity-50 inline-flex items-center gap-1">
                    {restoring === key ? <Loader2 size={11} className="animate-spin" /> : <RotateCcw size={11} />}
                    Restore
                  </button>
                )}
              </div>
              {isOpen && <DiffDetail kind={kind} name={name} row={v} source={source} projectSlug={projectSlug} />}
            </div>
          );
        })}
      </div>

      {/* Restore is PROJECT-WIDE (the engine rewrites every endpoint/service in
          the commit), so the scope is stated plainly before anything happens —
          the label must not promise a per-component rollback it cannot deliver. */}
      {confirm && (
        <div className="px-3 py-3 border-t border-card-border bg-amber-500/5">
          <div className="text-xs text-page-text flex items-start gap-1.5">
            <AlertTriangle size={13} className="mt-0.5 shrink-0 text-amber-500" />
            <div>
              <div className="font-medium">This restores the whole project.</div>
              <div className="text-page-text/70 mt-0.5">
                Commit <code className="font-mono">{confirm.short || shortSha(confirm.commit)}</code> will replace
                every endpoint and service definition in the paired Flow project.
              </div>
            </div>
          </div>
          <div className="flex gap-2 mt-2">
            <button type="button" onClick={() => doRestore(confirm)} disabled={!!restoring}
                    className="text-xs px-2.5 py-1 rounded bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50">
              {restoring ? "Restoring…" : "Restore anyway"}
            </button>
            <button type="button" onClick={() => setConfirm(null)} disabled={!!restoring}
                    className="text-xs px-2.5 py-1 rounded border border-card-border text-page-text/70">
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Say what was filtered and how deep the walk went, so a short list never
          looks like a history that silently lost entries. */}
      {/* State the DEPTH searched, so a short list never reads as a truncated one. */}
      {versions.length > 0 && state.data?.totalCommits > 0 && (
        <div className="px-3 py-1.5 text-[11px] text-page-text/40 border-t border-card-border">
          {versions.length} change{versions.length === 1 ? "" : "s"} across the project's
          full history ({state.data.totalCommits} commits)
        </div>
      )}
    </div>
  );
}
